import { format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import { Box, Tooltip, Typography } from '@mui/material'
import { PaymentMethodIconComponent } from '@src/components/LoanCalendar/PaymentMethodIconComponent'
import { EBankFailureCode, EOperationStatus, EPaymentMethod } from '@src/types/constants'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import { ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import LoanCalendarTableCellActions from './LoanCalendarTableCellActions'

export interface ITableColumn {
  field: string
  headerName: string
  width?: number
  align?: 'left' | 'center' | 'right'
  headerAlign?: 'left' | 'center' | 'right'
  editable?: boolean
  hideable?: boolean
  hidden?: boolean
  cellClassName?: string
  valueGetter: (row: OperationInformation<CustomerPaymentOperation>) => unknown
  valueFormatter?: (value: unknown) => string
  renderCell?: (row: OperationInformation<CustomerPaymentOperation>, value: unknown) => ReactNode
  render: (row: OperationInformation<CustomerPaymentOperation>) => ReactNode
}

export const useLoanCalendarTableColumns = (
  showMore: boolean,
): ITableColumn[] => {
  const { t } = useTranslation()

  const displayCorrectPaymentStatusIcon = useCallback(
    (operationStatus: EOperationStatus, bankFailureCode: EBankFailureCode | null, bankFailureDate: string | null) => {
      let content: ReactNode
      let tooltipTitle: string = t(`loan.payment.pmtStatus.${operationStatus}`)

      const isFailed = operationStatus === EOperationStatus.Failed
      const hasFailureDetails = isFailed && bankFailureCode

      if (hasFailureDetails) {
        tooltipTitle += ` - ${t(`enum.bankFailureCode.${bankFailureCode}`)}`
        content = (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="caption" color="error" fontWeight="bold">
              {bankFailureCode}
            </Typography>
            <Typography variant="caption" color="error" fontWeight="bold">
              {bankFailureDate}
            </Typography>
          </Box>
        )
      } else if (isFailed) {
        content = <CancelIcon color="error" />
      } else if (operationStatus === EOperationStatus.Planned) {
        content = <WatchLaterIcon color="action" />
      } else if (operationStatus === EOperationStatus.Complete) {
        content = <CheckCircleIcon color="success" />
      } else {
        content = <WatchLaterIcon color="warning" />
      }

      return <Tooltip title={tooltipTitle}>{content}</Tooltip>
    },
    [t],
  )

  const displayNegativeAmountAsZero = useCallback((value: number) => (value < 0 ? 0 : value), [])

  return useMemo<ITableColumn[]>(
    () => {
      const columns = [
      {
        field: 'paymentNumber',
        headerName: '#',
        width: 50,
        align: 'center',
        headerAlign: 'center',
        editable: false,
        hideable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.paymentNumber,
        valueFormatter: (value: unknown) => (value as number)?.toString() || '',
      },
      {
        field: 'paymentMethod',
        headerName: t('loan.payment.pmtModeTitle'),
        width: 60,
        align: 'center',
        headerAlign: 'center',
        editable: false,
        hideable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.paymentMethodId,
        renderCell: (_row: OperationInformation<CustomerPaymentOperation>, value: unknown) => (
          <PaymentMethodIconComponent paymentMode={value as EPaymentMethod} />
        ),
      },
      {
        field: 'plannedDate',
        headerName: t('loan.payment.plannedOnDate'),
        editable: false,
        hideable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.effectiveDate,
        valueFormatter: (value: unknown) => format(value as Date, 'P'),
      },
      {
        field: 'amount',
        headerName: t('loan.payment.amount'),
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.amount,
        valueFormatter: (value: unknown) => formatCurrency(value as string | number | null | undefined),
      },
      {
        field: 'status',
        headerName: t('loan.payment.status'),
        width: 120,
        align: 'center',
        headerAlign: 'center',
        editable: false,
        hideable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.operationStatusId,
        renderCell: (row: OperationInformation<CustomerPaymentOperation>, value: unknown) =>
          displayCorrectPaymentStatusIcon(
            (value ?? EOperationStatus.Planned) as EOperationStatus,
            row.operation.bankFailureCode,
            row.operation.bankFailureDate,
          ),
      },
      {
        field: 'interestPaid',
        headerName: t('loan.payment.plannedInterestAmount'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'strong',
        hidden: !showMore,
        hideable: true,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.interestPaid,
        valueFormatter: (value: unknown) => formatCurrency(value as string | number | null | undefined),
      },
      {
        field: 'principalPaid',
        headerName: t('loan.payment.principalAmount'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        hidden: !showMore,
        hideable: true,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.principalPaid,
        valueFormatter: (value: unknown) => formatCurrency(value as string | number | null | undefined),
      },
      {
        field: 'nsfPaid',
        headerName: t('loan.payment.nsfFee'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        hidden: !showMore,
        hideable: true,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.nsfPaid,
        valueFormatter: (value: unknown) => (value ? formatCurrency(value as string | number | null | undefined) : ''),
      },
      {
        field: 'otherFeePaid',
        headerName: t('loan.payment.otherFee'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        hidden: !showMore,
        hideable: true,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.otherFeePaid,
        valueFormatter: (value: unknown) => (value ? formatCurrency(value as string | number | null | undefined) : ''),
      },
      {
        field: 'advancedPrincipalAmount',
        headerName: t('loan.payment.advancedPrincipalAmount'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        hidden: !showMore,
        hideable: true,
        valueGetter: () => 0, // TODO: Implement data
        valueFormatter: (value: unknown) => (value ? formatCurrency(value as string | number | null | undefined) : ''),
      },
      {
        field: 'principalBalance',
        headerName: t('loan.payment.principalBalance'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'strong',
        hidden: !showMore,
        hideable: true,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.principalBalance,
        valueFormatter: (value: unknown) => formatCurrency(value as string | number | null | undefined),
      },
      {
        field: 'overdueAmount',
        headerName: t('loan.payment.overdueAmount'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) =>
          displayNegativeAmountAsZero(_.lateInformationResult.pastdue),
        valueFormatter: (value: unknown) => (value ? formatCurrency(value as string | number | null | undefined) : ''),
      },
      {
        field: 'latePrincipal',
        headerName: t('loan.payment.preview.latePrincipal'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) =>
          displayNegativeAmountAsZero((_.lateInformationResult?.overduePrincipal ?? 0)),
        valueFormatter: (value: unknown) => (value ? formatCurrency(value as string | number | null | undefined) : ''),
      },
      {
        field: 'loanBalance',
        headerName: t('loan.payment.loanBalance'),
        editable: false,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'strong',
        hideable: false,
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operationResultDto.loanBalance,
        valueFormatter: (value: unknown) => formatCurrency(value as string | number | null | undefined),
      },
      {
        field: 'description',
        headerName: t('loan.payment.description'),
        width: 250,
        editable: false,
        hideable: false,
        cellClassName: 'wrap',
        valueGetter: (_: OperationInformation<CustomerPaymentOperation>) => _.operation.note || '',
        valueFormatter: (value: unknown) => value as string,
      },
      {
        field: 'action',
        headerName: t('common.menu'),
        width: 125,
        align: 'right',
        headerAlign: 'right',
        editable: false,
        hideable: false,
        valueGetter: () => '',
        renderCell: (row: OperationInformation<CustomerPaymentOperation>) => (
          <LoanCalendarTableCellActions id={row.simulatedOperationId} />
        ),
      },
    ]

    // Add render function to each column
    return columns.map((col) => ({
      ...col,
      render: (row: OperationInformation<CustomerPaymentOperation>) => {
        const value = col.valueGetter(row)
        if (col.renderCell) {
          return col.renderCell(row, value)
        }
        if (col.valueFormatter) {
          return col.valueFormatter(value)
        }
        return String(value ?? '')
      },
    })) as ITableColumn[]
    },
    [t, displayCorrectPaymentStatusIcon, showMore, displayNegativeAmountAsZero],
  )
}

export const useTableColumnVisibility = (columns: ITableColumn[], showMore: boolean) => {
  return useMemo(
    () =>
      columns.reduce((acc, col) => {
        if (col.hideable) {
          acc[col.field] = col.hidden ? !col.hidden : showMore
        } else {
          acc[col.field] = true
        }
        return acc
      }, {} as Record<string, boolean>),
    [columns, showMore],
  )
}
